// followUpsPref.ts · v12.10.15 (#1637, LOCKED: several pills - All clears the others and any other clears All; the left-menu number is
//   the total of the picked pills, each follow-up counted once) · 2026-10-13 · VoiceSpectrum AI
// followUpsPref.ts · v12.10.12 (#1534) · 2026-10-10 · VoiceSpectrum AI
// Your account › Preferences › Follow-ups count: what the left-menu Follow-ups number counts, and which pills the
// Follow-ups view opens on. One choice per person, kept on the server (GET/PUT /t2/security/account/preferences).
// Default "today" (Tushar 10/9: "i would go just with today to keep it clear how many followup are due today").
//
// No react-query here on purpose: Your account mounts in its own root (openYourAccount) with no query provider, so the
// choice lives in one module cache and a window event tells Shell and Messages the moment it changes.
import { useEffect, useState } from 'react';
import api from './api';

export type FuBadgePref = 'overdue' | 'today' | 'week' | 'future' | 'all';
export const FU_PREF_CHOICES: { value: FuBadgePref; label: string }[] = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'future', label: 'Future' },
  { value: 'all', label: 'All' },
];
const EVENT = 'vs:fu-pref';
const PATH = '/t2/security/account/preferences';
const ORDER: FuBadgePref[] = FU_PREF_CHOICES.map((c) => c.value);
const DEFAULT: FuBadgePref[] = ['today'];

let cached: FuBadgePref[] | null = null;
let loading: Promise<FuBadgePref[]> | null = null;

/** v12.10.15 (#1637): the choice is a set, sent as "overdue,today". All alone, or the others without All; never empty. */
function norm(v: unknown): FuBadgePref[] {
  const parts = (Array.isArray(v) ? v : String(v ?? '').split(','))
    .map((x) => String(x).trim().toLowerCase()).filter((x): x is FuBadgePref => ORDER.includes(x as FuBadgePref));
  if (parts.includes('all')) return ['all'];
  const set = ORDER.filter((o) => parts.includes(o));
  return set.length ? set : [...DEFAULT];
}

/** Picking All clears the others; picking any other clears All; the last pill cannot be turned off. */
export function toggleFuPref(cur: FuBadgePref[], v: FuBadgePref): FuBadgePref[] {
  if (v === 'all') return ['all'];
  const base: FuBadgePref[] = cur.filter((x) => x !== 'all');
  const next: FuBadgePref[] = base.includes(v) ? base.filter((x) => x !== v) : [...base, v];
  return next.length ? ORDER.filter((o) => next.includes(o)) : cur;
}

export function fuPrefLabel(prefs: FuBadgePref[]): string {
  return prefs.map((p) => FU_PREF_CHOICES.find((c) => c.value === p)?.label ?? p).join(' + ');
}

export function getCachedFuPref(): FuBadgePref[] { return cached ?? [...DEFAULT]; }

export function loadFuPref(force = false): Promise<FuBadgePref[]> {
  if (cached && !force) return Promise.resolve(cached);
  if (loading && !force) return loading;
  loading = api.get<{ followUpsBadge?: string }>(PATH)
    .then((r) => { cached = norm(r?.followUpsBadge); window.dispatchEvent(new CustomEvent(EVENT, { detail: cached })); return cached; })
    .catch(() => { cached = cached ?? [...DEFAULT]; return cached; })
    .finally(() => { loading = null; });
  return loading;
}

export async function saveFuPref(v: FuBadgePref[]): Promise<FuBadgePref[]> {
  const r = await api.put<{ followUpsBadge?: string }>(PATH, { followUpsBadge: norm(v).join(',') });
  cached = norm(r?.followUpsBadge ?? v);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: cached }));
  return cached;
}

/** The person's choice - Today until the server answers, then live (changes in Your account arrive at once). */
export function useFuPref(): FuBadgePref[] {
  const [v, setV] = useState<FuBadgePref[]>(getCachedFuPref());
  useEffect(() => {
    // the same set again keeps the same array, so a reload does not move pills the person changed in the view
    const keep = (n: FuBadgePref[]) => setV((prev) => (prev.join() === n.join() ? prev : n));
    const on = (e: Event) => keep(norm((e as CustomEvent).detail));
    window.addEventListener(EVENT, on);
    void loadFuPref().then(keep);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return v;
}

/** The follow-up pills the view opens on (keys as MessagesPage fuPillDefs: overdue, today, tomorrow, wd<n>, nextweek, later,
 *  all) - the union of the picked choices. This week = today through Saturday; Future = tomorrow onward. */
export function fuPrefPills(prefs: FuBadgePref[], keys: string[]): string[] {
  if (prefs.includes('all')) return ['all'];
  const out = new Set<string>();
  for (const pref of prefs) {
    switch (pref) {
      case 'overdue': out.add('overdue'); break;
      case 'week': keys.filter((k) => !['overdue', 'nextweek', 'later', 'all'].includes(k)).forEach((k) => out.add(k)); break;
      case 'future': keys.filter((k) => !['overdue', 'today', 'all'].includes(k)).forEach((k) => out.add(k)); break;
      default: out.add('today');
    }
  }
  return keys.filter((k) => out.has(k));
}

/** The badge number for the choice, from /t2/surface/message-counts `followUps`. The buckets overlap (This week holds Today;
 *  Future holds the rest of this week), so the total is built from the four parts that do not: overdue, today, the rest of
 *  this week (week - today) and later (future - rest of week). Each follow-up is counted once. */
export function fuBadgeCount(prefs: FuBadgePref[], counts: Partial<Record<FuBadgePref, number>> | null | undefined, fallback = 0): number {
  if (!counts) return fallback;
  if (prefs.includes('all')) return counts.all ?? fallback;
  const today = counts.today ?? 0;
  const restWeek = Math.max(0, (counts.week ?? 0) - today);
  const later = Math.max(0, (counts.future ?? 0) - restWeek);
  const parts = new Set<string>();
  for (const p of prefs) {
    if (p === 'overdue') parts.add('overdue');
    if (p === 'today' || p === 'week') parts.add('today');
    if (p === 'week' || p === 'future') parts.add('restWeek');
    if (p === 'future') parts.add('later');
  }
  const n: Record<string, number> = { overdue: counts.overdue ?? 0, today, restWeek, later };
  return [...parts].reduce((t, k) => t + n[k], 0);
}
